/**
 * Page Context — registers semantic page metadata as PostHog super properties
 * on every route change. Attached to every subsequent event on that page,
 * including the auto-captured $pageview.
 *
 * Mount once at the app root (_app.tsx).
 */
import { useRouter } from 'next/router';
import * as React from 'react';

import { getPostHogIdentityProperties } from './identityProperties';
import { posthog } from './posthog';
import { isMemberAnalyticsPath } from './routePolicy';

export type PageType =
  | 'home'
  | 'post'
  | 'blog'
  | 'poll'
  | 'question'
  | 'group'
  | 'group_activity'
  | 'group_events'
  | 'group_members'
  | 'group_resources'
  | 'event_conference'
  | 'event_conference_sessions'
  | 'event_conference_session'
  | 'event_conference_expo'
  | 'event_conference_booth'
  | 'event_virtual'
  | 'event'
  | 'learning'
  | 'learning_collection'
  | 'quick_call'
  | 'quick_meet'
  | 'sponsor'
  | 'search'
  | 'messages'
  | 'members'
  | 'profile'
  | 'cart'
  | 'checkout'
  | 'membership'
  | 'notifications'
  | 'drafts'
  | 'tags'
  | 'site_page'
  | 'login'
  | 'activate'
  | 'unknown';

export interface PageContext {
  page_type: PageType;
  page_section: string;
  page_entity_id?: string;
  page_entity_type?: string;
  page_parent_entity_id?: string;
  page_parent_entity_type?: string;
}

const PRESENCE_COOLDOWN_MS = 30 * 60 * 1000;
const presenceCapturedAt = new Map<string, number>();

export function derivePageContext(
  pathname: string,
  query: Record<string, string | string[] | undefined>
): PageContext {
  const get = (key: string): string | undefined => {
    const v = query[key];
    return Array.isArray(v) ? v[0] : v;
  };

  if (pathname.match(/\/community\/post\/|^\/post\//))
    return {
      page_type: 'post',
      page_section: 'community',
      page_entity_id: get('postId'),
      page_entity_type: 'post',
    };

  if (pathname.startsWith('/blog/'))
    return {
      page_type: 'blog',
      page_section: 'community',
      page_entity_id: get('blogId'),
      page_entity_type: 'post',
    };

  if (pathname.startsWith('/poll/'))
    return {
      page_type: 'poll',
      page_section: 'community',
      page_entity_id: get('pollId'),
      page_entity_type: 'poll',
    };

  if (pathname.startsWith('/question/'))
    return {
      page_type: 'question',
      page_section: 'community',
      page_entity_id: get('questionId'),
      page_entity_type: 'question',
    };

  if (pathname.match(/\/community\/groups\/[^/]+\/activity/))
    return {
      page_type: 'group_activity',
      page_section: 'community',
      page_entity_id: get('groupId'),
      page_entity_type: 'group',
    };

  if (pathname.match(/\/community\/groups\/[^/]+\/events/))
    return {
      page_type: 'group_events',
      page_section: 'community',
      page_entity_id: get('groupId'),
      page_entity_type: 'group',
    };

  if (pathname.match(/\/community\/groups\/[^/]+\/members/))
    return {
      page_type: 'group_members',
      page_section: 'community',
      page_entity_id: get('groupId'),
      page_entity_type: 'group',
    };

  if (pathname.match(/\/community\/groups\/[^/]+\/resources/))
    return {
      page_type: 'group_resources',
      page_section: 'community',
      page_entity_id: get('groupId'),
      page_entity_type: 'group',
    };

  if (pathname.match(/\/community\/groups\/[^/]+/))
    return {
      page_type: 'group',
      page_section: 'community',
      page_entity_id: get('groupId'),
      page_entity_type: 'group',
    };

  if (pathname.match(/\/events\/conferences\/[^/]+\/sessions\/[^/]+/))
    return {
      page_type: 'event_conference_session',
      page_section: 'events',
      page_entity_id: get('sessionId'),
      page_entity_type: 'conference_session',
      page_parent_entity_id: get('eventId'),
      page_parent_entity_type: 'event',
    };

  if (pathname.match(/\/events\/conferences\/[^/]+\/booth\//))
    return {
      page_type: 'event_conference_booth',
      page_section: 'events',
      page_entity_id: get('boothId'),
      page_entity_type: 'conference_booth',
      page_parent_entity_id: get('eventId'),
      page_parent_entity_type: 'event',
    };

  if (pathname.match(/\/events\/conferences\/[^/]+\/sessions/))
    return {
      page_type: 'event_conference_sessions',
      page_section: 'events',
      page_entity_id: get('eventId'),
      page_entity_type: 'event',
    };

  if (pathname.match(/\/events\/conferences\/[^/]+\/expo/))
    return {
      page_type: 'event_conference_expo',
      page_section: 'events',
      page_entity_id: get('eventId'),
      page_entity_type: 'event',
    };

  if (pathname.match(/\/events\/conferences\/[^/]+/))
    return {
      page_type: 'event_conference',
      page_section: 'events',
      page_entity_id: get('eventId'),
      page_entity_type: 'event',
    };

  if (pathname.includes('/quick-call/'))
    return {
      page_type: 'quick_call',
      page_section: 'meetings',
      page_entity_id: get('slug') ?? get('quick-call-id'),
      page_entity_type: 'quick_call',
    };

  if (pathname.includes('/quick-meet/') || pathname.includes('/meetings/meet/'))
    return {
      page_type: 'quick_meet',
      page_section: 'meetings',
      page_entity_id: get('slug') ?? get('quickMeetId'),
      page_entity_type: 'quick_meet',
    };

  if (pathname.startsWith('/learning/collections'))
    return {
      page_type: 'learning_collection',
      page_section: 'learning',
      page_entity_id: get('collectionId'),
      page_entity_type: 'learning_collection',
    };

  if (pathname === '/learning' || pathname.startsWith('/learning/'))
    return { page_type: 'learning', page_section: 'learning' };

  if (pathname.match(/^\/events\/\[eventId\]/))
    return {
      page_type: pathname.includes('/meet') ? 'event_virtual' : 'event',
      page_section: 'events',
      page_entity_id: get('eventId'),
      page_entity_type: 'event',
    };

  if (pathname.match(/\/events\/[^/]+\/registrations/))
    return {
      page_type: 'event_conference',
      page_section: 'events',
      page_entity_id: pathname.split('/')[2],
      page_entity_type: 'event',
    };

  if (pathname.startsWith('/virtualEvent/'))
    return {
      page_type: 'event_virtual',
      page_section: 'events',
      page_entity_id: get('eventId'),
      page_entity_type: 'event',
    };

  if (pathname === '/events' || pathname.startsWith('/events?'))
    return { page_type: 'event_conference', page_section: 'events' };

  if (pathname.startsWith('/sponsors/'))
    return {
      page_type: 'sponsor',
      page_section: 'sponsors',
      page_entity_id: get('organizationId'),
      page_entity_type: 'organization',
    };

  if (pathname.match(/^\/members\/[^/]+/))
    return {
      page_type: 'profile',
      page_section: 'members',
      page_entity_id: pathname.split('/')[2],
      page_entity_type: 'profile',
    };

  if (pathname === '/members')
    return { page_type: 'members', page_section: 'members' };

  if (pathname === '/search')
    return { page_type: 'search', page_section: 'search' };

  if (pathname.startsWith('/messages'))
    return { page_type: 'messages', page_section: 'messages' };

  if (pathname.startsWith('/cart/checkout'))
    return { page_type: 'checkout', page_section: 'cart' };

  if (pathname.startsWith('/cart'))
    return { page_type: 'cart', page_section: 'cart' };

  if (pathname.match(/\/membership-orders\/[^/]+\/add-to-cart/))
    return {
      page_type: 'checkout',
      page_section: 'membership',
      page_entity_id: pathname.split('/')[2],
      page_entity_type: 'membership_plan',
    };

  if (pathname.match(/\/membership-orders\/[^/]+/))
    return {
      page_type: 'membership',
      page_section: 'membership',
      page_entity_id: pathname.split('/')[2],
      page_entity_type: 'membership_plan',
    };

  if (pathname.startsWith('/new-membership/'))
    return {
      page_type: 'membership',
      page_section: 'membership',
      page_entity_id: get('membershipTypeId'),
      page_entity_type: 'membership_plan',
    };

  if (pathname === '/memberships' || pathname.startsWith('/memberships'))
    return { page_type: 'membership', page_section: 'membership' };

  if (pathname === '/notifications')
    return { page_type: 'notifications', page_section: 'notifications' };

  if (pathname === '/drafts')
    return { page_type: 'drafts', page_section: 'community' };

  if (pathname.startsWith('/community/tags'))
    return { page_type: 'tags', page_section: 'community' };

  if (pathname === '/community' || pathname === '/')
    return { page_type: 'home', page_section: 'community' };

  if (pathname.startsWith('/site/'))
    return { page_type: 'site_page', page_section: 'site' };

  if (pathname.startsWith('/login') || pathname.startsWith('/v1/sso'))
    return { page_type: 'login', page_section: 'auth' };

  if (pathname.startsWith('/activate'))
    return { page_type: 'activate', page_section: 'auth' };

  return { page_type: 'unknown', page_section: 'unknown' };
}

export function usePageContext(disabled = false): void {
  const router = useRouter();
  const lastPresenceSignatureRef = React.useRef<string>();

  React.useEffect(() => {
    if (disabled || typeof window === 'undefined') return;
    if (!isMemberAnalyticsPath(window.location.pathname)) return;
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;

    const register = (pathname: string, query: typeof router.query) => {
      try {
        const ctx = derivePageContext(
          pathname,
          query as Record<string, string | string[] | undefined>
        );

        // Always unregister entity fields first to prevent stale values bleeding
        // from a previous page that had entity context
        posthog.unregister('page_entity_id');
        posthog.unregister('page_entity_type');
        posthog.unregister('page_parent_entity_id');
        posthog.unregister('page_parent_entity_type');

        const props: Record<string, string> = {
          page_type: ctx.page_type,
          page_section: ctx.page_section,
        };
        if (ctx.page_entity_id) props.page_entity_id = ctx.page_entity_id;
        if (ctx.page_entity_type) props.page_entity_type = ctx.page_entity_type;
        if (ctx.page_parent_entity_id)
          props.page_parent_entity_id = ctx.page_parent_entity_id;
        if (ctx.page_parent_entity_type)
          props.page_parent_entity_type = ctx.page_parent_entity_type;

        posthog.register(props);
        // Query-string changes (sorting, pagination, modal state) do not
        // represent a new business page. Deduplicate by semantic page/entity
        // and allow a new presence signal only after a meaningful return.
        const presenceSignature = JSON.stringify(props);
        if (lastPresenceSignatureRef.current === presenceSignature) return;
        lastPresenceSignatureRef.current = presenceSignature;

        const now = Date.now();
        const lastCapturedAt = presenceCapturedAt.get(presenceSignature) ?? 0;
        if (now - lastCapturedAt < PRESENCE_COOLDOWN_MS) return;
        presenceCapturedAt.set(presenceSignature, now);

        posthog.capture('user_presence', {
          $process_person_profile: false,
          activity_scope: 'presence',
          event_action: 'presence',
          route: pathname,
          pathname: window.location.pathname,
          source: 'frontend',
          ...props,
          ...getPostHogIdentityProperties(),
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Analytics] Page context tracking unavailable', error);
        }
      }
    };

    if (router.isReady) {
      register(router.pathname, router.query);
    }

    const handleRouteChange = () => {
      register(router.pathname, router.query);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [disabled, router, router.isReady, router.pathname, router.query, router.events]);
}
